import { z } from 'zod';

import { isLocalDateTime, percentToBps } from './auction-form-schema';
import { ANSWER_MAX, MAX_LOT_INTERVAL_MINUTES, MAX_SALE_DAYS } from './sale-rules';

export const SALE_HANDLE = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/;
const MINUTE = 60_000;
const WHOLE = /^\d{1,3}$/;

/**
 * The sale form. Times are `datetime-local` values on the store's clock; the caller resolves them
 * to instants with the store time zone. `scheduleEditable` is false once the sale is scheduled:
 * the schedule fields are then left alone and not checked.
 */
export function saleFormSchema(
  toInstant: (value: string) => number | null,
  scheduleEditable: boolean,
) {
  return z
    .object({
      title: z.string().trim().min(1, 'auctions:sales.validation.titleRequired').max(160),
      handle: z.string().trim(),
      subtitle: z.string().trim().max(240, 'auctions:sales.validation.subtitleLength'),
      coverMediaId: z.string().nullable(),
      essay: z.string(),
      viewing: z.string().trim().max(1000, 'auctions:sales.validation.viewingLength'),
      startsAt: z.string(),
      firstCloseAt: z.string(),
      lotIntervalMinutes: z.string().trim(),
      buyerPremiumPercent: z.string().trim(),
    })
    .superRefine((v, ctx) => {
      const issue = (path: string[], message: string) =>
        ctx.addIssue({ code: 'custom', path, message });
      if (!v.handle) issue(['handle'], 'auctions:sales.validation.handleRequired');
      else if (!SALE_HANDLE.test(v.handle)) issue(['handle'], 'auctions:sales.validation.handle');
      if (!scheduleEditable) return;

      const starts = isLocalDateTime(v.startsAt) ? toInstant(v.startsAt) : null;
      const closes = isLocalDateTime(v.firstCloseAt) ? toInstant(v.firstCloseAt) : null;
      if (starts === null) issue(['startsAt'], 'auctions:validation.dateTime');
      if (closes === null) issue(['firstCloseAt'], 'auctions:validation.dateTime');
      if (starts !== null && closes !== null) {
        if (closes - starts < MINUTE) {
          issue(['firstCloseAt'], 'auctions:sales.validation.closeAfterStart');
        } else if (closes - starts > MAX_SALE_DAYS * 24 * 60 * MINUTE) {
          issue(['firstCloseAt'], 'auctions:sales.validation.maxDuration');
        }
      }
      if (
        !WHOLE.test(v.lotIntervalMinutes) ||
        Number(v.lotIntervalMinutes) > MAX_LOT_INTERVAL_MINUTES
      ) {
        issue(['lotIntervalMinutes'], 'auctions:sales.validation.interval');
      }
      if (v.buyerPremiumPercent !== '') {
        const bps = percentToBps(v.buyerPremiumPercent);
        if (bps === null || bps < 1 || bps > 5000) {
          issue(['buyerPremiumPercent'], 'auctions:validation.premium');
        }
      }
    });
}

export type SaleFormInput = z.input<ReturnType<typeof saleFormSchema>>;
export type SaleFormOutput = z.output<ReturnType<typeof saleFormSchema>>;

export const answerSchema = z.object({
  answer: z
    .string()
    .trim()
    .min(1, 'auctions:questions.validation.answerRequired')
    .max(ANSWER_MAX, 'auctions:questions.validation.answerLength'),
});
export type AnswerFormInput = z.input<typeof answerSchema>;
export type AnswerFormOutput = z.output<typeof answerSchema>;
